/**
 * MOOS Custom — Product page: "Select size" gate + mobile price-in-button
 * ---------------------------------------------------------------------------
 * Behaviour (matches the Self-Portrait reference):
 *   - No size is pre-selected on load.
 *   - The Add-to-cart button is shown as a DISABLED "Select size" button until
 *     the shopper picks a size; then Prestige's own (re-rendered) button takes
 *     over ("Add to cart" / "Sold out", enabled/disabled as usual).
 *   - On mobile, the current price is shown INSIDE the button (label left,
 *     price right). Desktop keeps the price where it already is.
 *
 * Why it is built this way (so it survives future Prestige theme updates):
 *   - ZERO edits to Prestige's variant-picker / buy-buttons / option-value Liquid.
 *   - It only hooks Prestige's native events ('variant:change') and reads/writes
 *     DOM that Prestige already produces.
 *   - The selected-size styling is pure CSS :checked, so un-checking a radio in
 *     JS visually de-selects it with no extra work.
 *
 * To revert: remove the <script> tag for this file from layout/theme.liquid.
 */
(function () {
  'use strict';

  // Change this string (or wire a translation) if you want different wording.
  var SELECT_SIZE_LABEL = 'Select size';

  function priceText(scope) {
    var el = scope.querySelector('.price-list--product sale-price');
    if (!el) return '';
    var clone = el.cloneNode(true);
    clone.querySelectorAll('.sr-only').forEach(function (n) { n.remove(); });
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  function ensurePriceSpan(btn, scope) {
    var span = btn.querySelector('.moos-atc-price');
    if (!span) {
      span = document.createElement('span');
      span.className = 'moos-atc-price';
      span.setAttribute('aria-hidden', 'true');
      btn.appendChild(span);
    }
    span.textContent = priceText(scope);
  }

  // Move the button's plain text label into its own span so the price span can
  // sit beside it. If `text` is provided, the label is overwritten with it.
  function setLabel(btn, text) {
    var label = btn.querySelector('.moos-atc-label');
    if (!label) {
      var current = btn.textContent.trim();
      label = document.createElement('span');
      label.className = 'moos-atc-label';
      label.textContent = text != null ? text : current;
      btn.textContent = '';
      btn.appendChild(label);
    } else if (text != null) {
      label.textContent = text;
    }
  }

  function setupForm(productForm) {
    // The product's <variant-picker> lives in the same section (it is a SIBLING
    // of <product-form>, not a child), so we look it up from the section.
    var scope = productForm.closest('.shopify-section') || document;
    var picker = scope.querySelector('variant-picker');
    if (!picker) return; // product has no size options → leave Prestige's default

    // Marks the page as enhanced so CSS (e.g. hiding the standalone mobile price)
    // only applies when this script actually ran.
    document.documentElement.classList.add('moos-pdp-enhanced');

    function submitButton() {
      return productForm.querySelector('buy-buttons button[type="submit"]');
    }

    function showSelectSize() {
      var btn = submitButton();
      if (!btn) return;
      setLabel(btn, SELECT_SIZE_LABEL);
      ensurePriceSpan(btn, scope);
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      var bb = productForm.querySelector('buy-buttons');
      if (bb) bb.classList.add('moos-awaiting-size');
    }

    function showSelected() {
      var btn = submitButton();
      if (!btn) return;
      // Prestige re-rendered the button to the correct label/disabled state.
      // We only wrap its label and (re)inject the mobile price span.
      setLabel(btn, null);
      ensurePriceSpan(btn, scope);
      var bb = productForm.querySelector('buy-buttons');
      if (bb) bb.classList.remove('moos-awaiting-size');
    }

    // --- initial state: de-select every option + show "Select size" ----------
    // rAF so we run after Prestige's own variant-picker initialisation.
    requestAnimationFrame(function () {
      // De-select every pre-selected size in this section (covers both the main
      // picker and the sticky add-to-cart bar's picker).
      scope.querySelectorAll('variant-picker input[type="radio"]:checked').forEach(function (r) {
        r.checked = false;
      });
      showSelectSize();
    });

    // A valid variant was chosen → Prestige re-rendered buy-buttons; adopt it.
    productForm.addEventListener('variant:change', function () {
      requestAnimationFrame(showSelected);
    });
  }

  // In the stacked (grid) desktop layout the gallery scrolls vertically. Prestige's
  // own thumbnail handler assumes a HORIZONTAL carousel (its `isScrollable` check is
  // true because of our vertical overflow), so clicking a thumbnail does nothing.
  // We add a handler that scrolls only the gallery column to the matching image.
  function setupGalleryThumbnails() {
    document.querySelectorAll('product-gallery').forEach(function (gallery) {
      var container = gallery.querySelector('.product-gallery__carousel');
      if (!container) return;

      gallery.querySelectorAll('.product-gallery__thumbnail[data-media-id]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          // Only act when the gallery is a vertical scroll container (desktop);
          // on mobile it scrolls horizontally and Prestige handles it.
          if (container.scrollHeight <= container.clientHeight + 1) return;

          var id = thumb.getAttribute('data-media-id');
          var media = gallery.querySelector('.product-gallery__media[data-media-id="' + id + '"]');
          if (!media) return;

          var top = media.getBoundingClientRect().top
            - container.getBoundingClientRect().top
            + container.scrollTop;
          container.scrollTo({ top: top, behavior: 'smooth' });
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Info accordions -> right-side drawer (D5).
  // The 4 info rows (Description + the 3 accordion blocks) stay on the page
  // looking exactly as Prestige renders them, but clicking one opens a native
  // <x-drawer> containing all 4 as real accordions, with the clicked one open.
  //
  // Why it survives theme updates: it only READS the blocks Prestige already
  // renders (theme editor stays the single source of truth) and reuses the
  // theme's own <x-drawer> + .accordion components. To revert: delete this
  // function and its call in init(), and set the description block's
  // "collapse_content" back to false.
  //
  // Note: Prestige's <accordion-disclosure> binds its own click handler to the
  // <summary> in connectedCallback (it preventDefaults + toggles). A second
  // listener can't stop it, so for the ON-PAGE rows we replace the <summary>
  // node (which drops that listener) and then preventDefault the native
  // <details> toggle ourselves — the row no longer expands inline.
  function setupInfoDrawer() {
    var section = document.querySelector('.shopify-section--main-product');
    if (!section) return;
    var info = section.querySelector('.product-info');
    if (!info || document.getElementById('moos-info-drawer')) return;

    // Split the rows: a .moos-sizechart accordion (a page-backed block such as
    // Size chart) opens in its OWN drawer; every other accordion shares the info
    // drawer. Splitting first keeps the shared-drawer indices contiguous.
    var infoAccordions = [];
    var sizechartAcc = null;
    info.querySelectorAll('.accordion').forEach(function (acc) {
      if (acc.closest('.moos-sizechart')) { sizechartAcc = acc; }
      else { infoAccordions.push(acc); }
    });

    if (sizechartAcc) setupStandaloneDrawer(section, sizechartAcc);

    if (!infoAccordions.length) return;

    // Build the shared drawer (native <x-drawer>; opens via [aria-controls]).
    var drawer = document.createElement('x-drawer');
    drawer.id = 'moos-info-drawer';
    // scheme-1 = the theme's default scheme, which the cart drawer uses
    // (its color_scheme setting is empty -> default). This matches the cart
    // drawer's cream background + text instead of the white modal scheme.
    drawer.className = 'drawer color-scheme color-scheme--scheme-1 moos-info-drawer';

    var titleEl = section.querySelector('[data-block-type="title"]');
    // Read only the product title (.product-title), NOT the MOOS subtitle that sits in
    // the same title block — otherwise the drawer header reads "Olive NomadOlive Belted
    // Midi Dress" (subtitle + title concatenated) instead of just the title.
    var titleText = '';
    if (titleEl) {
      var titleName = titleEl.querySelector('.product-title');
      titleText = (titleName || titleEl).textContent.trim();
    }
    var header = document.createElement('span');
    header.className = 'h5';
    header.setAttribute('slot', 'header');
    header.textContent = titleText;
    drawer.appendChild(header);

    var body = document.createElement('div');
    body.className = 'v-stack moos-info-drawer__body';
    drawer.appendChild(body);

    function openDrawerAt(idx) {
      body.querySelectorAll('.accordion').forEach(function (clone, i) {
        var d = clone.querySelector('details');
        if (!d) return;
        var isTarget = i === idx;
        d.open = isTarget;
        d.setAttribute('aria-expanded', isTarget ? 'true' : 'false');
      });
    }

    infoAccordions.forEach(function (acc, i) {
      // Clone into the drawer FIRST (clone keeps the original summary, so it
      // gets full accordion behaviour when it connects). Start collapsed.
      var clone = acc.cloneNode(true);
      var cd = clone.querySelector('details');
      if (cd) { cd.open = false; cd.setAttribute('aria-expanded', 'false'); }
      body.appendChild(clone);

      // Turn the on-page row into a drawer trigger.
      var summary = acc.querySelector('summary');
      if (!summary) return;
      var fresh = summary.cloneNode(true);            // drops the theme's own listener
      summary.replaceWith(fresh);
      fresh.setAttribute('aria-controls', 'moos-info-drawer'); // native open
      fresh.style.cursor = 'pointer';
      acc.classList.add('moos-info-trigger');
      fresh.addEventListener('click', function (e) {
        e.preventDefault();                            // no inline expand
        openDrawerAt(i);                               // open the clicked one
      });
    });

    // Single-open: opening one accordion INSIDE the drawer closes the others (the theme's
    // accordions are otherwise independent). Listen for each details' toggle → when one
    // opens, collapse the rest. The `syncing` guard + the `!d.open` check prevent the
    // programmatic closes from re-triggering this handler.
    var syncing = false;
    body.querySelectorAll('.accordion').forEach(function (clone) {
      var d = clone.querySelector('details');
      if (!d) return;
      d.addEventListener('toggle', function () {
        if (syncing || !d.open) return;
        syncing = true;
        body.querySelectorAll('.accordion').forEach(function (other) {
          if (other === clone) return;
          var od = other.querySelector('details');
          if (od && od.open) {
            od.open = false;
            od.setAttribute('aria-expanded', 'false');
          }
        });
        syncing = false;
      });
    });

    section.appendChild(drawer); // outside .product-info so CSS scoping stays clean
  }

  // A page-backed accordion (e.g. Size chart) gets its OWN drawer showing only
  // its content — it looks like one of the info rows but does not join the shared
  // info drawer. Content is cloned from the on-page accordion so the theme editor
  // stays the single source of truth.
  function setupStandaloneDrawer(section, acc) {
    if (document.getElementById('moos-sizechart-drawer')) return;
    var summary = acc.querySelector('summary');
    var content = acc.querySelector('.accordion__content');
    if (!summary || !content) return;

    var title = summary.textContent.trim();

    var drawer = document.createElement('x-drawer');
    drawer.id = 'moos-sizechart-drawer';
    drawer.className = 'drawer color-scheme color-scheme--scheme-1 moos-sizechart-drawer';

    var header = document.createElement('span');
    header.className = 'h5';
    header.setAttribute('slot', 'header');
    header.textContent = title;
    drawer.appendChild(header);

    var bodyWrap = document.createElement('div');
    // Keep .prose for page-backed rich text, but DROP it for the size-guide component:
    // Prestige's `.prose :where(:not(meta,span)+*){margin-block-start}` rules (incl. a
    // 1.25rem desktop variant) add sibling top-margins that misalign the guide's IN/CM
    // row and push the table down. The guide is fully self-styled, so it doesn't need prose.
    var hasSizeGuide = content.querySelector('size-guide-moos');
    bodyWrap.className = 'moos-sizechart-drawer__body' + (hasSizeGuide ? '' : ' prose');
    bodyWrap.innerHTML = content.innerHTML;
    drawer.appendChild(bodyWrap);

    section.appendChild(drawer);

    // On-page row → open this standalone drawer (no inline expand; the content is
    // hidden by the shared .moos-info-trigger CSS).
    var fresh = summary.cloneNode(true);
    summary.replaceWith(fresh);
    fresh.setAttribute('aria-controls', 'moos-sizechart-drawer');
    fresh.style.cursor = 'pointer';
    acc.classList.add('moos-info-trigger');
    fresh.addEventListener('click', function (e) { e.preventDefault(); });
  }

  // Size the product drawers (info drawer + size-chart drawer) to the product-info
  // column: the panel's left edge lines up with the column's left edge so it fully
  // covers it. Measured because the column is a %-of-container width. Inline style
  // wins over the theme's --drawer-max-width. Re-run on resize.
  function applyDrawerWidths() {
    var section = document.querySelector('.shopify-section--main-product');
    if (!section) return;
    var info = section.querySelector('.product-info');
    if (!info) return;

    var rect = info.getBoundingClientRect();
    if (!rect.width) return; // column not laid out (e.g. hidden) → skip
    var width = Math.round(window.innerWidth - rect.left);

    // Only the INFO drawer is width-matched to the product-info column. The size-chart
    // drawer is intentionally half-screen (desktop) / full-width (mobile) via CSS
    // (custom-moos.css §12) so the size table and the figure can sit side by side like
    // Self-Portrait — so it is NOT measured/narrowed here.
    var drawers = [];
    var infoDrawer = document.getElementById('moos-info-drawer');
    if (infoDrawer) drawers.push(infoDrawer);

    drawers.forEach(function (d) {
      d.style.setProperty('--drawer-max-width', width + 'px');
    });
  }

  var resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyDrawerWidths, 150);
  }

  function init() {
    // document.querySelectorAll('product-form').forEach(setupForm);

    // Gate the ATC only once Prestige's variant-picker is live. If theme.js fails,
    // the server-rendered form (size pre-selected, Add to cart enabled) still works.
    if (window.customElements) {
      customElements.whenDefined('variant-picker').then(function () {
        document.querySelectorAll('product-form').forEach(setupForm);
      });
    }
    setupGalleryThumbnails();
    setupInfoDrawer();
    applyDrawerWidths();
    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
